import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import DeviceController from "@/controllers/device-controller";

import styles from "./atlas-empty-state.scss";
import "@/components/display/atlas-heading/atlas-heading";
import "@/components/display/atlas-icon/atlas-icon";
import "@/components/display/atlas-illustration/atlas-illustration";
import "@/components/display/atlas-text/atlas-text";

export type EmptyStateProps = {
    "icon": string;
    "illustration": string;
    "illustration-alt": string;
    "header": string;
    "description": string;
    "small": boolean;
};

/**
 * @dependency atlas-heading
 * @dependency atlas-icon
 * @dependency atlas-illustration
 * @dependency atlas-text
 *
 * @prop {string} icon - Ícone exibido no empty-state
 * @prop {string} illustration - Ilustração exibida no empty state
 * @prop {string} illustration-alt - Descrição de acessibilidade para a ilustração
 * @prop {string} icon - Ícone exibido no empty-state
 * @prop {string} header - Título do empty-state
 * @prop {string} description - Descrição do empty-state
 * @prop {boolean} small - Indica se deve exibir a variação pequena do empty-state
 *
 * @tag atlas-empty-state
 */
@customElement("atlas-empty-state")
export default class AtlasEmptyState extends LitElement {
    static styles = styles;

    @property({ type: String }) icon: string;

    @property({ type: String }) illustration: string;

    @property({ type: String, attribute: "illustration-alt" }) illustrationAlt: string;

    @property({ type: String }) header: string;

    @property({ type: String }) description: string;

    @property({ type: Boolean }) small: boolean;

    private _deviceController = new DeviceController(this);

    renderIllustrationOrIcon() {
        return when(
            !!this.illustration,
            () => html`
                <atlas-illustration
                    name=${this.illustration}
                    alt=${this.illustrationAlt}
                    size=${this._deviceController.isMobile ? "sm" : "md"}
                ></atlas-illustration>
            `,
            () => html`<atlas-icon name=${this.icon} theme="primary" size=${this.small ? "5x" : "6x"}></atlas-icon>`
        );
    }

    renderMessage() {
        return when(
            !!this.description,
            () => html`<atlas-text size=${this.small ? "xsm" : "md"} muted>${this.description}</atlas-text>`,
            () => html`<atlas-text size=${this.small ? "xsm" : "md"} muted><slot></slot></atlas-text>`
        );
    }

    render() {
        const emptyStateClass = {
            "empty-state": true,
            "small": this.small
        };

        return html`
            <div class=${classMap(emptyStateClass)}>
                ${this.renderIllustrationOrIcon()}
                <atlas-heading size=${this.small ? "h6" : "h5"} alignment="center" muted ?hidden=${!this.header}>
                    ${this.header}
                </atlas-heading>
                ${this.renderMessage()}
                <slot name="button"></slot>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-empty-state": AtlasEmptyState;
    }
}
